import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  RateBars,
  Picker,
  ScrollRow,
  StatusPill,
  TrendChart,
} from "@breenova/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { PageHeader } from "@/components/app-shell";
import { api } from "@/lib/api";
import { seasonLabel, useBreedingSeasons } from "@/lib/breeding";

function unwrap<T>(data: T | undefined, error: unknown, what: string): T {
  if (error || data === undefined) {
    throw new Error(`Could not load ${what}.`);
  }

  return data;
}

function useOverview() {
  return useQuery({
    queryKey: ["stats", "overview"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/stats/overview");
      return unwrap(data, error, "the overview");
    },
  });
}

/*
 * Keyed by the season, not by its year. A year holds as many seasons as the breeder runs
 * — indoor birds down in January, outdoor birds in April — and the two do not perform
 * alike. Averaging them would hide exactly the difference they exist to record.
 */
function useSeasonStats(seasonId: string | undefined) {
  return useQuery({
    queryKey: ["stats", "season", seasonId ?? null],
    enabled: seasonId !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/stats/season/{id}", {
        params: { path: { id: seasonId! } },
      });

      return unwrap(data, error, "the season statistics");
    },
  });
}

function usePairStats(seasonId: string | undefined) {
  return useQuery({
    queryKey: ["stats", "pairs", seasonId ?? null],
    enabled: seasonId !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/stats/pairs", {
        params: { query: seasonId !== undefined ? { breedingSeasonId: seasonId } : {} },
      });

      return unwrap(data, error, "the pair statistics");
    },
  });
}

export function StatisticsPage() {
  const overview = useOverview();
  const seasons = useBreedingSeasons();
  const [seasonId, setSeasonId] = useState<string | undefined>(undefined);

  const selectedSeasonId = seasonId ?? overview.data?.currentSeasonId ?? undefined;

  const season = useSeasonStats(selectedSeasonId);
  const pairs = usePairStats(selectedSeasonId);

  return (
    <div className="flex flex-col gap-md">
      <PageHeader
        title="Statistics"
        subtitle="What the aviary is doing"
        toolbar={
          <div className="w-full md:w-56">
            <Picker
              label="Season"
              emptyLabel="No season recorded yet"
              value={selectedSeasonId ?? ""}
              onChange={setSeasonId}
              options={(seasons.data?.seasons ?? []).map((option) => ({
                value: option.id,
                label: seasonLabel(option),
                ...(option.isCurrent ? { hint: "Current season" } : {}),
              }))}
            />
          </div>
        }
      />

      <section className="px-5 md:px-gutter-wide">
        {overview.isPending ? (
          <p className="text-body text-on-surface-variant">Loading…</p>
        ) : overview.isError || !overview.data ? (
          <Alert tone="error">Could not load the overview.</Alert>
        ) : (
          <ScrollRow
            aria-label="Aviary totals"
            // Four tiles fit a desktop row outright, so the swipe affordance retires.
            className="gap-sm md:grid md:grid-cols-4 md:overflow-x-visible"
          >
            <Tile label="Birds" value={overview.data.birdsInAviary} hint="in the aviary" />
            <Tile label="Active pairs" value={overview.data.activePairs} />
            <Tile label="Nests running" value={overview.data.activeNests} />
            <Tile
              label="In withdrawal"
              value={overview.data.birdsInWithdrawal}
              // The one tile that is a warning rather than a fact.
              tone={overview.data.birdsInWithdrawal > 0 ? "critical" : undefined}
              hint={overview.data.birdsInWithdrawal > 0 ? "not to be shown or sold" : undefined}
            />
          </ScrollRow>
        )}
      </section>

      {/*
        * Two columns of cards from `xl`.
        *
        * Every card here is a fixed-height panel — a rate bar chart, a trend, a table of
        * a dozen pairs — so on a wide screen a single column means scrolling past a lot
        * of white space to compare two figures that would fit side by side.
        */}
      <section className="flex flex-col gap-md px-5 pb-lg md:px-gutter-wide xl:grid xl:grid-cols-2 xl:items-start">
        {season.isPending && selectedSeasonId !== undefined ? (
          <p className="text-body text-on-surface-variant">Loading season…</p>
        ) : season.isError ? (
          <Alert tone="info">No statistics for that season yet.</Alert>
        ) : season.data ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle>{seasonLabel(season.data)} season</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-lg">
                <ScrollRow aria-label="Season totals" className="gap-sm" data-numeric>
                  <Tile label="Eggs" value={season.data.totals.eggsLaid} />
                  <Tile label="Hatched" value={season.data.totals.hatchedEggs} />
                  <Tile label="Ringed" value={season.data.totals.chicksRinged} />
                  <Tile
                    label="Clutch size"
                    value={
                      season.data.totals.averageClutchSize === null ||
                      season.data.totals.averageClutchSize === undefined
                        ? "—"
                        : season.data.totals.averageClutchSize.toFixed(1)
                    }
                  />
                </ScrollRow>

                <RateBars
                  bars={[
                    {
                      label: "Fertility",
                      value: season.data.totals.fertilityRate,
                      detail: `${season.data.totals.fertileEggs} of ${season.data.totals.eggsLaid}`,
                    },
                    {
                      label: "Hatch",
                      value: season.data.totals.hatchRate,
                      detail: `${season.data.totals.hatchedEggs} of ${season.data.totals.fertileEggs}`,
                    },
                    {
                      label: "Weaning",
                      value: season.data.totals.weaningRate,
                      detail: `${season.data.totals.chicksRinged} of ${season.data.totals.hatchedEggs}`,
                    },
                    {
                      label: "Nest mortality",
                      value: season.data.totals.mortalityRate,
                      detail: `${season.data.totals.chicksDied} of ${season.data.totals.hatchedEggs}`,
                    },
                  ]}
                />
              </CardContent>
            </Card>

            {season.data.speciesBreakdown.length > 1 ? (
              <Card>
                <CardHeader>
                  <CardTitle>By species</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-lg">
                  {/* An aviary's overall fertility says little across species — they do
                      not perform alike, and averaging them hides both. */}
                  {season.data.speciesBreakdown.map((species) => (
                    <div key={species.speciesId} className="flex flex-col gap-sm">
                      <p className="text-label-caps uppercase text-on-surface-variant">
                        {species.speciesName}
                      </p>
                      <RateBars
                        bars={[
                          { label: "Fertility", value: species.rates.fertilityRate },
                          { label: "Hatch", value: species.rates.hatchRate },
                          { label: "Weaning", value: species.rates.weaningRate },
                        ]}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : null}
          </>
        ) : null}

        {overview.data && overview.data.recentSeasons.length > 1 ? (
          <Card>
            <CardHeader>
              <CardTitle>Recent seasons</CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart
                firstLabel="Eggs"
                secondLabel="Ringed"
                points={overview.data.recentSeasons.map((entry) => ({
                  label: String(entry.year),
                  first: entry.rates.eggsLaid,
                  second: entry.rates.chicksRinged,
                }))}
              />
            </CardContent>
          </Card>
        ) : null}

        {pairs.data && pairs.data.pairs.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Pairs</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-data text-on-surface" data-numeric>
                <thead>
                  <tr className="text-label-caps uppercase text-on-surface-variant">
                    <th scope="col" className="py-xs text-left">Pair</th>
                    <th scope="col" className="py-xs text-right">Eggs</th>
                    <th scope="col" className="py-xs text-right">Fertile</th>
                    <th scope="col" className="py-xs text-right">Ringed</th>
                  </tr>
                </thead>
                <tbody>
                  {pairs.data.pairs.map((pair) => (
                    <tr key={pair.pairId} className="border-t border-outline-variant">
                      <td className="py-sm">
                        <span className="block">
                          {pair.sireRingNumber ?? "Unringed"} × {pair.damRingNumber ?? "Unringed"}
                        </span>
                        {pair.isHybridPair ? (
                          <span className="text-label-caps uppercase text-on-surface-variant">
                            Mule pair
                          </span>
                        ) : null}
                      </td>
                      <td className="py-sm text-right">{pair.rates.eggsLaid}</td>
                      <td className="py-sm text-right">
                        {pair.rates.fertilityRate === null || pair.rates.fertilityRate === undefined
                          ? "—"
                          : `${Math.round(pair.rates.fertilityRate * 100)}%`}
                      </td>
                      <td className="py-sm text-right">{pair.rates.chicksRinged}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ) : null}
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | string;
  hint?: string | undefined;
  tone?: "critical" | undefined;
}) {
  return (
    // Fixed width while the row scrolls; the grid's own column from `md`, where a 144px
    // tile in a 300px cell would sit against the left edge of its own share of the row.
    <li className="flex w-36 shrink-0 flex-col gap-xs rounded-card bg-surface-container-lowest p-md shadow-card md:w-auto">
      <span className="text-label-caps uppercase text-on-surface-variant">{label}</span>
      <span className="text-page-title text-on-surface" data-numeric>
        {value}
      </span>
      {hint ? (
        tone === "critical" ? (
          <StatusPill tone="critical">{hint}</StatusPill>
        ) : (
          <span className="text-label-caps uppercase text-on-surface-variant">{hint}</span>
        )
      ) : null}
    </li>
  );
}
